import React, { useEffect } from "react";
import type { ManifestAsset, ManifestVariant } from "../types.js";
import { toFetchUrl, variantFilename } from "../download.js";
import { pickPreviewVariant } from "../thumbnail.js";

interface Props {
  asset: ManifestAsset;
  onClose: () => void;
  /** Admin-only: opens the edit/replace/remove dialog for this asset. */
  onEdit?: (asset: ManifestAsset) => void;
  /** Signed-in curators only: mint a magic link for just this file. */
  onMagicLink?: (asset: ManifestAsset) => void;
}

export function DownloadModal({ asset, onClose, onEdit, onMagicLink }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Group variants by format so the user picks: format → size
  const byFormat = new Map<string, ManifestVariant[]>();
  for (const v of asset.variants) {
    if (!byFormat.has(v.format)) byFormat.set(v.format, []);
    byFormat.get(v.format)!.push(v);
  }
  for (const arr of byFormat.values()) arr.sort((a, b) => a.width - b.width);

  // Prefer the 1200 webp for the popup image — it's one of the two sizes the
  // droplet mirrors (thumbnail + this), so it renders instantly from cache.
  // Larger/original variants are NOT cached server-side; they only stream on a
  // download click. Rasterized previews of documents/videos (migrated EPS/PDF
  // renders, video posters) qualify too; undefined when nothing is renderable.
  const preview = pickPreviewVariant(asset);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{asset.name}</h2>
          <div className="modal-header-actions">
            {onMagicLink && (
              <button
                className="dl-edit-btn dl-magic-btn"
                title="Create a shareable magic link for this file"
                onClick={() => onMagicLink(asset)}
              >
                Magic link
              </button>
            )}
            {onEdit && (
              <button className="dl-edit-btn" onClick={() => onEdit(asset)}>
                Edit
              </button>
            )}
            <button className="close" aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>
        </header>
        <div className="modal-body">
          {preview && (
            <img className="preview" src={preview.url} alt={asset.name} />
          )}
          <dl className="metadata">
            {asset.sku && (
              <>
                <dt>SKU</dt>
                <dd>{asset.sku}</dd>
              </>
            )}
            {asset.productCategory && (
              <>
                <dt>Category</dt>
                <dd>{asset.productCategory}</dd>
              </>
            )}
            {asset.region && (
              <>
                <dt>Region</dt>
                <dd>{asset.region}</dd>
              </>
            )}
            {asset.assetType && (
              <>
                <dt>Type</dt>
                <dd>{asset.assetType}</dd>
              </>
            )}
          </dl>
          <h3>Download</h3>
          <div className="variants">
            {[...byFormat.entries()].map(([format, variants]) => (
              <div key={format} className="variant-group">
                <h4>{format.toUpperCase()}</h4>
                <ul>
                  {variants.map((v) => (
                    <li key={`${v.width}-${v.format}`}>
                      {/* origin-relative href + explicit filename so the saved
                          file keeps the real asset name (same-origin is required
                          for the download filename to be honored). */}
                      <a href={toFetchUrl(v.url)} download={variantFilename(asset, v)}>
                        {v.width === 0 ? "Original" : `${v.width}px`}
                      </a>
                      {v.bytes && <span className="bytes">{formatBytes(v.bytes)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
